import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createContext, useContext, type ReactNode } from "react";
import { usarParametro } from "../../../shared/navegacao/navegacao";
import type { CampanhaDaLeitura, CentralGateway, CorpoDasMetas } from "../infrastructure/central.gateway";

const Contexto = createContext<CentralGateway | null>(null);

const PERIODOS = ["dia", "semana", "mes"] as const;
export type PeriodoDaCentral = (typeof PERIODOS)[number];

export function periodoDaUrl(valor: string | null): PeriodoDaCentral {
  return PERIODOS.find((item) => item === valor) ?? "semana";
}

export const chavesDaCentral = {
  visaoGeral: ["central", "visao-geral"] as const,
  detalhe: ["central", "conta"] as const,
  acessos: ["central", "acessos"] as const,
  autores: ["central", "autores"] as const,
  leituraDaIa: ["central", "leitura-ia"] as const,
};

/** Injecao do gateway: HTTP em producao, memoria no teste. */
export function ProvedorDaCentral({
  gateway,
  children,
}: {
  readonly gateway: CentralGateway;
  readonly children: ReactNode;
}) {
  return <Contexto.Provider value={gateway}>{children}</Contexto.Provider>;
}

function useGateway(): CentralGateway {
  const gateway = useContext(Contexto);
  if (!gateway) throw new Error("A Central precisa estar dentro de <ProvedorDaCentral>.");
  return gateway;
}

/** Visao geral da Central; consulta de novo a cada 5 min, porque a coleta e diaria. */
export function useVisaoGeral() {
  const gateway = useGateway();
  const periodo = periodoDaUrl(usarParametro("periodo"));
  return useQuery({
    queryKey: [...chavesDaCentral.visaoGeral, periodo],
    queryFn: () => gateway.visaoGeral(periodo),
    refetchInterval: 5 * 60_000,
  });
}

/** Detalhe da conta aberta. Os parâmetros `cliente` e `campanha` vêm da URL. */
export function useDetalheDaConta() {
  const gateway = useGateway();
  const cliente = usarParametro("cliente");
  const campanha = usarParametro("campanha");
  const tipo = usarParametro("tipo");
  return useQuery({
    queryKey: [...chavesDaCentral.detalhe, cliente, campanha, tipo],
    queryFn: () =>
      tipo
        ? gateway.detalheDaConta(cliente ?? "", campanha ?? "", tipo)
        : gateway.detalheDaConta(cliente ?? "", campanha ?? ""),
    enabled: Boolean(cliente),
  });
}

function useInvalidar(chave: readonly unknown[]) {
  const cliente = useQueryClient();
  return () => cliente.invalidateQueries({ queryKey: chave });
}

/** Grava as metas e pede o detalhe de novo. */
export function useSalvarMetas() {
  const gateway = useGateway();
  const invalidar = useInvalidar(chavesDaCentral.detalhe);
  return useMutation({
    mutationFn: (corpo: CorpoDasMetas) => gateway.salvarMetas(corpo),
    onSuccess: () => invalidar(),
  });
}

/** Corrige o tipo da campanha e pede o detalhe e a visão de novo: a meta que casa muda junto. */
export function useCorrigirTipo() {
  const gateway = useGateway();
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({ campanha, tipoCodigo }: { readonly campanha: CampanhaDaLeitura; readonly tipoCodigo: string }) =>
      gateway.corrigirTipo(campanha, tipoCodigo),
    onSuccess: () => cliente.invalidateQueries({ queryKey: ["central"] }),
  });
}

/** Última leitura da IA da campanha aberta. */
export function useLeituraDaIa(campanha: CampanhaDaLeitura) {
  const gateway = useGateway();
  return useQuery({
    queryKey: [...chavesDaCentral.leituraDaIa, campanha.clienteId, campanha.plataforma, campanha.campanha],
    queryFn: () => gateway.leituraDaIa(campanha),
  });
}

/** Gera a leitura do dia e pede a leitura de novo. */
export function useGerarLeituraDaIa() {
  const gateway = useGateway();
  const invalidar = useInvalidar(chavesDaCentral.leituraDaIa);
  return useMutation({
    mutationFn: ({ cliente, campanha }: { readonly cliente: string; readonly campanha: string }) =>
      gateway.gerarLeituraDaIa(cliente, campanha),
    onSuccess: () => invalidar(),
  });
}

/** Marca o alerta como visto e pede a visão de novo. */
export function useMarcarAlertaComoVisto() {
  const gateway = useGateway();
  const invalidar = useInvalidar(chavesDaCentral.visaoGeral);
  return useMutation({
    mutationFn: (id: string) => gateway.marcarAlertaComoVisto(id),
    onSuccess: () => invalidar(),
  });
}

/** Resolve o alerta com uma nota e pede a visão de novo. */
export function useResolverAlerta() {
  const gateway = useGateway();
  const invalidar = useInvalidar(chavesDaCentral.visaoGeral);
  return useMutation({
    mutationFn: ({ id, nota }: { readonly id: string; readonly nota: string }) => gateway.resolverAlerta(id, nota),
    onSuccess: () => invalidar(),
  });
}

/** Quem abriu a Central. */
export function useAcessosDaCentral() {
  const gateway = useGateway();
  return useQuery({
    queryKey: chavesDaCentral.acessos,
    queryFn: () => gateway.acessos(),
  });
}

/** Nomes autorizados a alterar conta sem cair em "alteração não identificada". */
export function useAutoresAutorizados() {
  const gateway = useGateway();
  return useQuery({
    queryKey: chavesDaCentral.autores,
    queryFn: () => gateway.autores(),
  });
}

/** Substitui a lista inteira e pede os autores de novo. */
export function useSalvarAutores() {
  const gateway = useGateway();
  const invalidar = useInvalidar(chavesDaCentral.autores);
  return useMutation({
    mutationFn: (autores: string[]) => gateway.salvarAutores(autores),
    onSuccess: () => invalidar(),
  });
}
